import { useState } from 'react';
import { ChoiceRow, NarrativeTextField } from '@/components/molecules';
import { STANDARD_RACES, type RaceChoice, type StandardRace } from '@/schemas/race';
import { normalizeOptionalTextInput } from '@/utils/textInput';
import { useBuilderDispatch, useBuilderDraft } from '../contexts/useBuilder';

// Re-design Fase 2 PR 4: <ChoiceRow> per le 5 razze preset + 1 row "Razza custom"
// con input border-bottom inline (pattern .field del mockup, non ancora estratto
// come <Field> molecule — vedi decisione F1 del WIP PR 4).

const RACE_DESCRIPTIONS: Record<StandardRace, string> = {
  umano: 'Adattabili e versatili. Curiosi, ambiziosi.',
  nano: 'Resilienti, legati alla pietra e al focolare. Lenti a fidarsi.',
  elfo: 'Antichi, attenti, sospesi tra natura e magia.',
  halfling: 'Piccoli, schivi, sorprendentemente coraggiosi.',
  mezzorco: 'Tra due mondi. Forti, fraintesi, sempre divisi.',
};

const CUSTOM_KEY = '__custom__';

/**
 * Step 2 — Selettore razza.
 *
 * 5 preset standard + opzione custom con input testo inline. Lo schema
 * `RaceChoice` (discriminated union 'preset' | 'custom') gestisce entrambi i
 * casi. Razza e archetipo sono scelte indipendenti: cambiare razza NON
 * azzera `archetypeId`. **FI-83 (2026-05-24)**: l'archetipo viene però
 * "auto-bumpato" al tipico della nuova razza SE quello corrente era il
 * tipico della razza vecchia (preserva scelte manuali divergenti). Logica
 * in `<SectionArchetype>` + `computeArchetypeAutoSelect`.
 */
export function SectionRace() {
  const draft = useBuilderDraft();
  const dispatch = useBuilderDispatch();

  const initialCustomName = draft.race?.kind === 'custom' ? draft.race.name : '';
  const [customName, setCustomName] = useState(initialCustomName);
  // **Bugfix 2026-05-22 (review FI-76)**: la card "Razza custom" deve essere
  // selezionabile visivamente PRIMA che l'utente scriva il nome, ma la sezione
  // race NON deve risultare completa finché il nome non è inserito.
  // `customClicked` traccia la selezione UI in locale; il dispatch al builder
  // avviene SOLO quando il nome è non-vuoto (così `draft.race === undefined`
  // → predicate sezione `race !== undefined` correttamente false → sezione
  // resta marcata incompleta).
  const [customClicked, setCustomClicked] = useState<boolean>(draft.race?.kind === 'custom');

  const customSelected = customClicked;
  const presetId = computePresetId(draft.race, customClicked);

  function selectPreset(id: StandardRace) {
    setCustomClicked(false);
    dispatch({ type: 'SET_RACE', race: { kind: 'preset', id } });
  }

  function selectCustom() {
    setCustomClicked(true);
    // BugFix 2026-05-23: usa `normalizeOptionalTextInput` per check vuoto
    // ma SALVA il valore raw (preserva spazi durante typing).
    const normalized = normalizeOptionalTextInput(customName);
    if (normalized !== null) {
      const race: RaceChoice = { kind: 'custom', name: normalized };
      dispatch({ type: 'SET_RACE', race });
    } else if (draft.race !== undefined) {
      // L'utente clicca custom senza testo, ma il draft conteneva una scelta
      // precedente (preset o custom da ricarica): la rimuoviamo per coerenza
      // col fatto che la sezione è ora "in attesa di nome".
      dispatch({ type: 'SET_RACE', race: undefined });
    }
  }

  function handleCustomNameChange(value: string) {
    setCustomName(value);
    // FI-50 follow-up: rimosso `onFocus={selectCustom}` dall'input (delegato
    // al molecule `NarrativeTextField`). Scrivere nel campo attiva
    // automaticamente la selezione custom: l'utente che tabba al field e
    // digita un nome ottiene UX naturale "scrivere == selezionare la card".
    setCustomClicked(true);
    // BugFix 2026-05-23: salva il valore RAW (no trim) per non distruggere
    // gli spazi durante typing. Vedi `utils/textInput.ts`.
    const normalized = normalizeOptionalTextInput(value);
    if (normalized !== null) {
      dispatch({ type: 'SET_RACE', race: { kind: 'custom', name: normalized } });
    } else if (draft.race !== undefined) {
      // Utente ha svuotato il testo dopo aver scritto qualcosa → la sezione
      // torna incompleta (no dispatch di placeholder).
      dispatch({ type: 'SET_RACE', race: undefined });
    }
  }

  return (
    <div className="flex flex-col gap-[0.4rem]">
      {STANDARD_RACES.map((raceId) => (
        <ChoiceRow
          key={raceId}
          title={<span className="capitalize">{raceLabel(raceId)}</span>}
          description={RACE_DESCRIPTIONS[raceId]}
          selected={presetId === raceId}
          onClick={() => selectPreset(raceId)}
        />
      ))}
      <ChoiceRow
        asContainer
        title="Razza custom"
        description="Goblin, golem, fungo parlante… concordala con il GM."
        selected={customSelected}
        onClick={selectCustom}
      >
        <div className="mt-[0.4rem]">
          <NarrativeTextField
            id={`race-${CUSTOM_KEY}`}
            label="Nome della razza"
            value={customName}
            onChange={handleCustomNameChange}
            required={customSelected}
            placeholder="Es. Goblin di palude"
          />
        </div>
      </ChoiceRow>
    </div>
  );
}

function raceLabel(id: StandardRace): string {
  if (id === 'mezzorco') return "Mezz'orco";
  return id;
}

/**
 * Risolve `presetId` per la lista di ChoiceRow preset: `null` se l'utente
 * ha cliccato "Razza custom" (anche senza ancora aver scritto il nome) o
 * se il draft non contiene una preset. Estratto per evitare ternario annidato.
 */
function computePresetId(
  race: RaceChoice | undefined,
  customClicked: boolean,
): StandardRace | null {
  if (customClicked) return null;
  if (race?.kind === 'preset') return race.id;
  return null;
}
